import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Container, Page, Card, Button, Stack, Modal } from "@project-mgmt/ui";
import { TaskUtils } from "@entities/task";
import { useTask, useTaskComments, CommentSection, TaskForm } from "@project-mgmt/feature-tasks";
import { useProject } from "@project-mgmt/feature-projects";
import { useWorkspaceDetails } from "@project-mgmt/feature-workspaces";
import { FileManager } from "@project-mgmt/feature-files";
import { EmptyState } from "../components/ErrorState";
import { LoadingSpinner } from "../components/LoadingSpinner";
import { notify } from "../providers/ToastProvider";
import { routes } from "../utils/routes";
import type { TaskRouteParams } from "../types/routes";
import { useAuth } from "@project-mgmt/feature-auth";

/**
 * TaskDetailPage
 *
 * Displays task details including:
 * - Task information (title, description, status, priority)
 * - Task comments
 * - Task files
 * - Task assignments
 * - Navigation to task files
 */
export function TaskDetailPage() {
  const navigate = useNavigate();
  const { workspaceId, projectId, taskId } = useParams<TaskRouteParams>();
  // Fetch data independently using feature hooks
  const { data: workspaceData, isLoading: isLoadingWorkspace } = useWorkspaceDetails(workspaceId || "");
  const { isLoading: isLoadingProject } = useProject(projectId || "", workspaceId || "");
  const { data: task, isLoading: isLoadingTask, error: taskError } = useTask(taskId || "", workspaceId || "");
  const {
    comments,
    isLoading: isLoadingComments,
    addComment,
    updateComment,
    deleteComment,
  } = useTaskComments(taskId || "", workspaceId || "");

  const { user } = useAuth();
  const currentUserId = user?.id || "";

  const [showComments, setShowComments] = useState(true);
  const [showEditForm, setShowEditForm] = useState(false);

  // Validate required params
  if (!workspaceId || !projectId || !taskId) {
    return <EmptyState message="Workspace, Project, or Task ID not found." />;
  }

  // Show loading state
  if (isLoadingWorkspace || isLoadingProject || isLoadingTask) {
    return <LoadingSpinner />;
  }

  // Handle errors
  if (taskError || !task) {
    return (
      <Container size="xl">
        <Page>
          <Card className="p-6 text-center">
            <p className="mb-4 text-red-600">{taskError instanceof Error ? taskError.message : "Task not found"}</p>
            <Button onClick={() => navigate(routes.projectTasks(workspaceId, projectId))}>Back to Tasks</Button>
          </Card>
        </Page>
      </Container>
    );
  }

  // Handle comment submission
  const handleAddComment = async (content: string) => {
    const result = await addComment(content);
    if (result.success) {
      notify.success("Comment added successfully");
    } else {
      notify.error(result.error || "Failed to add comment");
    }
  };

  // Handle comment update
  const handleUpdateComment = async (commentId: string, content: string) => {
    const result = await updateComment(commentId, content);
    if (result.success) {
      notify.success("Comment updated successfully");
    } else {
      notify.error(result.error || "Failed to update comment");
    }
  };

  // Handle comment deletion
  const handleDeleteComment = async (commentId: string) => {
    const result = await deleteComment(commentId);
    if (result.success) {
      notify.success("Comment deleted successfully");
    } else {
      notify.error(result.error || "Failed to delete comment");
    }
  };

  // Handle task update
  const handleTaskUpdate = async () => {
    setShowEditForm(false);
    notify.success("Task updated successfully");
    // Cache will be invalidated by the hook
  };

  // Get task status and priority colors
  const statusColor = TaskUtils.getStatusColor(task.status);
  const priorityColor = TaskUtils.getPriorityColor(task.priority);
  const isOverdue = TaskUtils.isOverdue(task);
  const isDueSoon = TaskUtils.isDueSoon(task);
  const daysUntilDue = TaskUtils.getDaysUntilDue(task);
  const taskAssignedUser = workspaceData?.members.find((member) => member.user.id === task.assignedTo);

  return (
    <Container size="xl">
      <Page>
        {/* Task Header */}
        <Card className="mb-4 p-4 sm:mb-6 sm:p-6">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="min-w-0 flex-1">
              <div className="mb-3 flex flex-wrap items-center gap-2">
                <h1 className="break-words text-2xl font-bold sm:text-3xl">{task.title}</h1>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium sm:text-sm ${statusColor} whitespace-nowrap bg-opacity-10`}
                >
                  {TaskUtils.getStatusDisplay(task.status)}
                </span>
                <span
                  className={`rounded-full px-3 py-1 text-xs font-medium sm:text-sm ${priorityColor} whitespace-nowrap bg-opacity-10`}
                >
                  {TaskUtils.getPriorityDisplay(task.priority)} Priority
                </span>
                {isOverdue && (
                  <span className="whitespace-nowrap rounded-full bg-red-100 px-3 py-1 text-xs font-medium text-red-700 sm:text-sm">
                    Overdue
                  </span>
                )}
                {isDueSoon && !isOverdue && (
                  <span className="whitespace-nowrap rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-700 sm:text-sm">
                    Due Soon
                  </span>
                )}
              </div>
              {task.description && (
                <p className="text-textSecondary mb-4 break-words text-base sm:text-lg">{task.description}</p>
              )}
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500 sm:gap-4 sm:text-sm">
                <span>Created {new Date(task.createdAt).toLocaleDateString()}</span>
                {task.dueDate && (
                  <span className={isOverdue ? "font-medium text-red-600" : ""}>
                    Due {new Date(task.dueDate).toLocaleDateString()}
                    {daysUntilDue !== null && !isOverdue && ` (${daysUntilDue} days)`}
                  </span>
                )}
                {task.amount && (
                  <span>
                    Amount: ${task.amount}
                    {task.transactionType && ` (${task.transactionType})`}
                  </span>
                )}
              </div>
            </div>
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row">
              <Button onClick={() => setShowEditForm(true)} variant="outline" className="w-full sm:w-auto">
                Edit
              </Button>
              <Button
                onClick={() => navigate(routes.taskFiles(workspaceId, projectId, taskId))}
                className="w-full sm:w-auto"
              >
                View Files
              </Button>
            </div>
          </div>
        </Card>

        <Stack spacing="lg">
          {/* Task Details */}
          <Card className="p-4 sm:p-6">
            <h2 className="text-textPrimary mb-4 text-lg font-semibold sm:text-xl">Task Details</h2>
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label className="text-textSecondary mb-1 block text-xs font-medium sm:text-sm">Status</label>
                <p className={`text-base font-semibold sm:text-lg ${statusColor}`}>
                  {TaskUtils.getStatusDisplay(task.status)}
                </p>
              </div>
              <div>
                <label className="text-textSecondary mb-1 block text-xs font-medium sm:text-sm">Priority</label>
                <p className={`text-base font-semibold sm:text-lg ${priorityColor}`}>
                  {TaskUtils.getPriorityDisplay(task.priority)}
                </p>
              </div>
              {task.dueDate && (
                <div>
                  <label className="text-textSecondary mb-1 block text-xs font-medium sm:text-sm">Due Date</label>
                  <p className="text-textPrimary text-base sm:text-lg">{new Date(task.dueDate).toLocaleDateString()}</p>
                  {daysUntilDue !== null && (
                    <p className={`text-xs sm:text-sm ${isOverdue ? "text-red-600" : "text-gray-500"}`}>
                      {isOverdue ? `${Math.abs(daysUntilDue)} days overdue` : `${daysUntilDue} days remaining`}
                    </p>
                  )}
                </div>
              )}
              {task.assignedTo && (
                <div>
                  <label className="text-textSecondary mb-1 block text-xs font-medium sm:text-sm">Assigned To</label>
                  <p className="text-textPrimary break-words text-base sm:text-lg">
                    {taskAssignedUser ? taskAssignedUser.user.phoneNumber : task.assignedTo}
                  </p>
                  <p className="text-textSecondary text-xs sm:text-sm">
                    {taskAssignedUser?.user.name ? taskAssignedUser.user.name : "This user has no name set"}
                  </p>
                </div>
              )}
              {task.amount && (
                <div>
                  <label className="text-textSecondary mb-1 block text-xs font-medium sm:text-sm">Amount</label>
                  <p className="text-textPrimary text-base sm:text-lg">
                    ${task.amount}
                    {task.transactionType && (
                      <span className="text-textSecondary ml-2 text-xs sm:text-sm">({task.transactionType})</span>
                    )}
                  </p>
                </div>
              )}
              <div>
                <label className="text-textSecondary mb-1 block text-xs font-medium sm:text-sm">Created</label>
                <p className="text-textPrimary text-base sm:text-lg">{new Date(task.createdAt).toLocaleDateString()}</p>
              </div>
              <div>
                <label className="text-textSecondary mb-1 block text-xs font-medium sm:text-sm">Last Updated</label>
                <p className="text-textPrimary text-base sm:text-lg">{new Date(task.updatedAt).toLocaleDateString()}</p>
              </div>
            </div>
          </Card>

          {/* Task Comments */}
          <Card className="p-4 sm:p-6">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-textPrimary text-lg font-semibold sm:text-xl">Comments ({comments.length})</h2>
              <Button variant="outline" size="sm" onClick={() => setShowComments(!showComments)}>
                {showComments ? "Hide" : "Show"}
              </Button>
            </div>
            {showComments && (
              <CommentSection
                comments={comments}
                onAddComment={handleAddComment}
                onUpdateComment={handleUpdateComment}
                onDeleteComment={handleDeleteComment}
                isLoading={isLoadingComments}
                currentUserId={currentUserId}
              />
            )}
          </Card>

          {/* Task Files */}
          <Card className="p-4 sm:p-6">
            <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-textPrimary text-lg font-semibold sm:text-xl">Files</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate(routes.taskFiles(workspaceId, projectId, taskId))}
                className="w-full sm:w-auto"
              >
                Full Screen View
              </Button>
            </div>
            <FileManager
              workspaceId={workspaceId!}
              taskId={taskId}
              onFileUpload={() => {
                notify.success("File uploaded successfully");
              }}
              onFileDownload={() => {
                notify.success("Download started");
              }}
              onFileDelete={() => {
                notify.success("File deleted successfully");
              }}
              showUpload={true}
            />
          </Card>

          {/* Quick Actions */}
          <Card className="p-4 sm:p-6">
            <h2 className="text-textPrimary mb-4 text-lg font-semibold sm:text-xl">Quick Actions</h2>
            <div className="flex flex-col gap-2 sm:flex-row sm:flex-wrap sm:gap-3">
              <Button
                variant="outline"
                onClick={() => navigate(routes.projectTasks(workspaceId, projectId))}
                className="w-full sm:w-auto"
              >
                Back to Tasks
              </Button>
              <Button
                variant="outline"
                onClick={() => navigate(routes.project(workspaceId, projectId))}
                className="w-full sm:w-auto"
              >
                View Project
              </Button>
            </div>
          </Card>
        </Stack>

        {/* Edit Task Modal */}
        <Modal isOpen={showEditForm} onClose={() => setShowEditForm(false)} title="Edit Task">
          <TaskForm
            workspaceId={workspaceId}
            projectId={projectId}
            taskId={taskId}
            workspaceMembers={workspaceData?.members || []}
            initialData={{
              title: task.title,
              description: task.description || "",
              priority: task.priority,
              dueDate: task.dueDate || "",
              amount: task.amount,
              transactionType: task.transactionType || "",
              assignedTo: task.assignedTo || "",
            }}
            onSuccess={handleTaskUpdate}
            onCancel={() => setShowEditForm(false)}
            notificationService={notify}
          />
        </Modal>
      </Page>
    </Container>
  );
}
